var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * ResourceNamePicker
 *
 * The "Resource Name" selector legacy renders above a resource's calendar when that calendar was
 * opened from the FM Dashboard (`resourcedashboard.html:14-19`, shown only when
 * `fromFMDashboard`, i.e. state `grm-resourcedashboardfm` =
 * `/resourcedashboard/:alias/:role/:managerAlias`). It lets a functional/resource manager step
 * through their team's calendars without returning to the dashboard.
 *
 * Legacy wiring reproduced here:
 * - options  `apiService.OneProfile.GetEngineerDetails(managerAlias)` → `directReporteesDetails`,
 *            sorted by display name (`calendar.controller.js:3099-3104`)
 * - on change `LoadResourceCalendar()` re-enters the same FM route with the newly selected alias
 *            and the SAME `managerAlias` (`calendar.controller.js:2968-2976`) — the manager
 *            segment must survive the hop or the next route match is a different state.
 *
 * Presentational + parent-owned navigation, matching `CalendarViewSwitcher`: this component owns
 * only the option list; the parent decides what selecting a resource means. `loadReportees` is a
 * DI seam so tests and local preview never touch the FXP shell.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FMOneProfileService from '../../../../services/FMOneProfile.service';
import { styles } from './ResourceNamePicker.styles';
const SELECT_ID = 'yearly-calendar-resource-name';
export const ResourceNamePicker = ({ managerAlias, selectedAlias, onSelectResource, loadReportees, }) => {
    const [reportees, setReportees] = useState([]);
    useEffect(() => {
        const manager = (managerAlias || '').trim();
        if (!manager) {
            setReportees([]);
            return undefined;
        }
        // Guards against a slower earlier response overwriting a newer manager's list.
        let cancelled = false;
        const load = loadReportees !== null && loadReportees !== void 0 ? loadReportees : ((alias) => FMOneProfileService.getSingleton().getDirectReportees(alias));
        Promise.resolve()
            .then(() => load(manager))
            .then((result) => {
            if (!cancelled) {
                setReportees(Array.isArray(result) ? result : []);
            }
        })
            .catch(() => {
            // The picker is an accelerator, never the only way to reach a calendar. If OneProfile is
            // unavailable we render nothing rather than an empty control the user cannot use.
            if (!cancelled) {
                setReportees([]);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [managerAlias, loadReportees]);
    const normalisedSelection = (selectedAlias || '').toLowerCase();
    const hasSelection = useMemo(() => reportees.some((r) => r.alias === normalisedSelection), [reportees, normalisedSelection]);
    const handleChange = useCallback((event) => {
        const alias = event.target.value;
        if (alias && alias !== normalisedSelection) {
            onSelectResource(alias);
        }
    }, [normalisedSelection, onSelectResource]);
    if (reportees.length === 0) {
        return null;
    }
    return (React.createElement("div", { style: styles.container, "data-testid": "resource-name-picker" },
        React.createElement("label", { htmlFor: SELECT_ID, style: styles.label }, "Resource Name"),
        React.createElement("select", { id: SELECT_ID, style: styles.select, value: hasSelection ? normalisedSelection : '', onChange: handleChange },
            hasSelection ? null : React.createElement("option", { value: "" }, "Select a resource"),
            reportees.map((r) => (React.createElement("option", { key: r.alias, value: r.alias }, r.displayName || r.alias))))));
};
ResourceNamePicker.displayName = 'ResourceNamePicker';
export default ResourceNamePicker;
//# sourceMappingURL=ResourceNamePicker.js.map